import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Col, Row } from 'react-bootstrap';
import { Container, FormGroup, Label } from 'reactstrap';
import { Breadcrumbs, Box, FormControlLabel, Tooltip, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';

import PaginationTableWithFuncBtn from '../../../components/Util/PaginationTableWithFuncBtn';
import ProjectSelector from '../../../components/Common/ProjectSelector';
import { GreenSwitch } from '../../../components/Common/GreenSwitch';
import ScanScheduleEditModal from './ScanScheduleEditModal';
import toastAlert from '../../../js/Base/ToastAlert';
import { fetchCompanyLicense } from '../../../store/slices/licenseSlice';
import { cloudNativeEnabledSelector, crowdstrikeEnabledSelector } from '../../../store/selectors/licenseSelectors';
import {
  getProjectScanSchedules,
  updateProjectScanSchedule,
  updateProjectScanEnabled,
} from '../../../api/scanScheduleApi';
import '../../../../src/style/css/_project.css';

const CROWDSTRIKE = 'CROWDSTRIKE';

const pad2 = (n) => String(n).padStart(2, '0');

const RUN_STATUS_LABELS = {
  RUNNING: '執行中',
  SUCCESS: '成功',
  FAILED: '失敗',
};

const renderLastRun = (s) => {
  if (!s.last_fired_at) return '尚未執行';

  const statusLabel = s.last_run_status ? `（${RUN_STATUS_LABELS[s.last_run_status] || s.last_run_status}）` : '';
  const text = `${moment(s.last_fired_at).local().format('YYYY-MM-DD HH:mm')}${statusLabel}`;
  const color = s.last_run_status === 'FAILED' ? '#D32F2F' : undefined;
  if (!s.last_error) return color ? <span style={{ color }}>{text}</span> : text;

  // 錯誤訊息可能很長（後端上限 2000 字），放在 tooltip 以免撐開欄位
  return (
    <Tooltip
      title={<span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{s.last_error}</span>}
      placement="top"
      arrow
      describeChild
      enterTouchDelay={0}
      slotProps={{ tooltip: { sx: { maxWidth: 480, fontSize: '12px' } } }}
    >
      <span tabIndex={0} style={{ color, textDecoration: 'underline dotted', cursor: 'help' }}>
        {text}
      </span>
    </Tooltip>
  );
};

export default function ScanScheduleSetting() {
  const dispatch = useDispatch();
  const [schedules, setSchedules] = useState([]);
  const [scanEnabled, setScanEnabled] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const cloudNativeEnabled = useSelector(cloudNativeEnabledSelector);
  const crowdstrikeEnabled = useSelector(crowdstrikeEnabledSelector);

  useEffect(() => {
    dispatch(fetchCompanyLicense());
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    const projectUuid = localStorage.getItem('project_uuid');
    const acctEmail = localStorage.getItem('user_email');
    try {
      const data = await getProjectScanSchedules(projectUuid, acctEmail);
      if (data.success) {
        setSchedules(data.schedules || []);
        setScanEnabled(data.scan_enabled);
      } else {
        toastAlert.autoClose(true).showError(data.msg || '發生錯誤，請稍後再試');
        setSchedules([]);
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    }
  };

  /** 授權未開通的類別不顯示，與 ConfigKey 頁一致 */
  const isLicensed = (scanType) =>
    scanType === CROWDSTRIKE ? crowdstrikeEnabled : cloudNativeEnabled;

  const licensedSchedules = schedules.filter((s) => isLicensed(s.scan_type));
  const configurable = licensedSchedules.filter((s) => s.has_credential);
  const missingCredential = licensedSchedules.filter((s) => !s.has_credential);

  const handleToggleProjectScan = async () => {
    const next = !scanEnabled;
    try {
      const data = await updateProjectScanEnabled({
        project_uuid: localStorage.getItem('project_uuid'),
        acct_email: localStorage.getItem('user_email'),
        scan_enabled: next,
      });
      if (data.success) {
        setScanEnabled(data.scan_enabled);
        toastAlert.autoClose(true).showSuccess(data.scan_enabled ? '已啟用專案掃描' : '已停用專案掃描');
      } else {
        toastAlert.autoClose(true).showError(data.msg || '設定失敗，請稍後再試');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    }
  };

  const handleToggleSchedule = async (scheduleUuid) => {
    const target = configurable.find((s) => s.uuid === scheduleUuid);
    if (!target) return;

    try {
      const data = await updateProjectScanSchedule({
        project_uuid: localStorage.getItem('project_uuid'),
        acct_email: localStorage.getItem('user_email'),
        scan_type: target.scan_type,
        is_enabled: !target.is_enabled,
      });
      if (data.success) {
        toastAlert.autoClose(true).showSuccess(data.schedule.is_enabled ? '已啟用訂閱' : '已停用訂閱');
        fetchSchedules();
      } else {
        toastAlert.autoClose(true).showError(data.msg || '設定失敗，請稍後再試');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    }
  };

  const handleEdit = (scheduleUuid) => {
    const target = configurable.find((s) => s.uuid === scheduleUuid) || null;
    setSelectedSchedule(target);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedSchedule(null);
  };

  const col_name = ['掃描項目', '憑證', '執行週期', '資料月份', '下次執行', '上次執行', '訂閱', '動作'];

  const data_frame = [
    { keyName: 'scan_label' },
    { keyName: 'credential_label' },
    { keyName: 'cycle_label' },
    { keyName: 'scan_month_label' },
    { keyName: 'next_run_label' },
    { keyName: 'last_run_label' },
    { isSlide: true },
    { keyName: 'func', isClick: true, editBtn: true },
  ];

  const col_size = ['22%', '9%', '14%', '8%', '13%', '14%', '10%', '10%'];

  const rowData = configurable.map((s) => ({
    ...s,
    scan_label: s.description ? `${s.label}（${s.description}）` : s.label,
    credential_label: s.credential_count > 1 ? `已設定 × ${s.credential_count}` : '已設定',
    cycle_label: `每月 ${s.day_of_month} 日 ${pad2(s.hour)}:${pad2(s.minute)}`,
    scan_month_label: s.scan_month_rule === 'PREVIOUS' ? '上個月' : '當月',
    next_run_label: s.next_run_at ? moment(s.next_run_at).local().format('YYYY-MM-DD HH:mm') : '-',
    last_run_label: renderLastRun(s),
    // PaginationTableWithFuncBtn 的 isSlide 分支寫死看 is_cancel，這裡做反向映射即可沿用
    is_cancel: !s.is_enabled,
  }));

  return (
    <React.Fragment>
      <Container className="mt-2">
        <div>
          <Row className="mb-2">
            <Col md={9}>
              <Breadcrumbs
                className="link-area"
                style={{ padding: '0 0 20px 0', fontWeight: 'bold' }}
                separator={<NavigateNextIcon style={{ fontSize: '20px', fontWeight: 'bold' }} />}
                aria-label="breadcrumb"
              >
                <Link
                  to="/admin-management"
                  style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}
                >
                  <p style={{ marginBottom: '0px' }}>網站管理員設定</p>
                </Link>
                <Link
                  to="/project/scan-schedule"
                  style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}
                >
                  <p style={{ marginBottom: '0px' }}>掃描排程訂閱</p>
                </Link>
              </Breadcrumbs>
            </Col>
            <Col md={3} style={{ justifyContent: 'right', alignContent: 'right' }}>
              <FormGroup>
                <Label style={{ fontSize: '12px', color: '#1D1D1D' }}>
                  <strong>當前專案</strong>
                </Label>
                <ProjectSelector
                  projectSelect={() => {}}
                  disabled={true}
                  style={{ fontSize: '12px', color: '#1d1d1d' }}
                />
              </FormGroup>
            </Col>
          </Row>

          <div>
            <Card>
              <Card.Header
                style={{
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <strong
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    fontFamily: 'Noto Sans, sans-serif',
                  }}
                >
                  掃描排程訂閱
                </strong>
                <FormControlLabel
                  checked={scanEnabled}
                  onChange={handleToggleProjectScan}
                  control={<GreenSwitch />}
                  label={
                    <Typography sx={{ fontSize: '14px' }}>
                      {scanEnabled ? '專案掃描已啟用' : '專案掃描已停用'}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ marginRight: 0 }}
                />
              </Card.Header>

              <Card.Body style={{ padding: 0 }}>
                {!scanEnabled && (
                  <Box
                    sx={{
                      mt: 2,
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: '#FFF4E5',
                      border: '1px solid #FFD8A8',
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', color: '#8A5300' }}>
                      此專案已停用掃描，以下排程一律不會執行。
                    </Typography>
                  </Box>
                )}

                {configurable.length > 0 ? (
                  <Box sx={scanEnabled ? {} : { opacity: 0.5, pointerEvents: 'none' }}>
                    <PaginationTableWithFuncBtn
                      rowData={rowData}
                      colName={col_name}
                      dataFrame={data_frame}
                      col_size={col_size}
                      editModalOpen={handleEdit}
                      switchOnChange={handleToggleSchedule}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography sx={{ fontSize: '13px', color: '#767676' }}>
                      此專案尚未設定任何雲端憑證，無法建立掃描排程。
                    </Typography>
                  </Box>
                )}

                {/* 共用表格元件無法逐列 disable 按鈕，因此無憑證的項目改列在表格外 */}
                {missingCredential.length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#767676' }}>
                      尚未設定憑證，無法排程：
                      {missingCredential.map((s) => s.label).join('、')}
                      {'  '}
                      <Link to="/project/cloud-config-key" style={{ fontSize: '13px' }}>
                        前往設定 ConfigKey
                      </Link>
                    </Typography>
                  </Box>
                )}
              </Card.Body>

              {isEditModalOpen && (
                <ScanScheduleEditModal
                  isModalOpen={isEditModalOpen}
                  handleClose={handleEditModalClose}
                  schedule={selectedSchedule}
                  submitEffect={fetchSchedules}
                />
              )}
            </Card>
          </div>
        </div>
      </Container>
    </React.Fragment>
  );
}
